import React, { useState, useEffect } from 'react';
import InputMapper from './util/InputMapper';

const Form = React.forwardRef(
    (
        {
            data,
            isNew,
            initDataIfNew,
            formModel,
            compose,
            onApplyFormState,
            setEdited,
            errors,
            hookFxns,
            onMount,
            children,
            compact,
            menuPortalDocument,
            noMarginReadOnly,
            formFieldBasis
        },
        ref
    ) => {
        const [formState, updateFormState] = useState({});
        // Find required inputs with string inputs
        const requiredStringFields = React.useMemo(() => {
            return (formModel ?? []).reduce((acc, conf) => {
                if (conf.trimOnSave) {
                    acc.push(conf.accessor);
                }
                return acc;
            }, []);
        }, [formModel]);

        React.useImperativeHandle(
            ref,
            () => ({
                getFormState: () => {
                    return formState;
                },
                updateFormState: (...props) => {
                    return onChange(...props);
                }
            }),
            [formState, onChange]
        );

        if (isNew && initDataIfNew) {
            data = {};
        }

        /**
         * Listener to parent controller call event, gathers data and passes upward.
         */
        useEffect(() => {
            if (compose) {
                const formStateCopy = { ...formState };

                // Trim string fields
                for (let i = 0; i < requiredStringFields.length; i++) {
                    if (formStateCopy[requiredStringFields[i]]) {
                        formStateCopy[requiredStringFields[i]] = formStateCopy[requiredStringFields[i]]
                            .replace(/\s/g, ' ')
                            .trim();
                        onChange(requiredStringFields[i], formStateCopy[requiredStringFields[i]]);
                    }
                }
                onApplyFormState(formStateCopy);
            }
        });

        /**
         * Runs once on mount, and fires onMount config function if present
         */
        useEffect(() => {
            // onFormMount from UI config
            if (typeof onMount === 'function') {
                onMount(data, hookFxns);
            }
        }, []);

        /**
         * Function accepts standardized {name, value} arguments to allow for simple document edits on standard structure of doc: {[name]: value}.
         * @param name (name of field, accessor, document property)
         * @param value (new value)
         * @param isHook (for use in chained onChange events triggered by config hooks
         */
        function onChange(name, value, isHook = false) {
            let formModelSubject = formModel.find((v) => v.accessor === name);
            if (
                typeof formModelSubject === 'object' &&
                typeof formModelSubject.preChangeBind === 'function' &&
                !isHook
            ) {
                formModelSubject.preChangeBind(value, onChange, hookFxns);
            }
            updateFormState((formState) => ({ ...formState, ...{ [name]: value } }));
            if (
                typeof formModelSubject === 'object' &&
                typeof formModelSubject.postChangeBind === 'function' &&
                !isHook
            ) {
                formModelSubject.postChangeBind(value, onChange, hookFxns);
            }

            // This is where we would place client side validation
            if (isNew) {
                // TODO: Apply incremental validation here
                setEdited(true);
            } else {
                setEdited(true);
            }
        }

        // Local consolidation of redux data state, and local changes in form state
        const formData = { ...data, ...formState };
        if (typeof children === 'function') {
            // This is handling for a headless data controller, in which only data state and
            // props are managed by the Form element. All display is managed directly via a
            // displayOverride component.
            return (
                <div>
                    <InputMapper
                        model={formModel}
                        errors={errors}
                        menuPortalDocument={menuPortalDocument}
                        noMarginReadOnly={noMarginReadOnly}
                        data={formData}
                        onChange={onChange}
                        compact={compact}
                        formFieldBasis={formFieldBasis}
                        headless
                    >
                        {(childProps) => children(childProps)}
                    </InputMapper>
                </div>
            );
        } else {
            // Form Elements mapped automatically with default layout
            return (
                <div>
                    <InputMapper
                        model={formModel}
                        errors={errors}
                        menuPortalDocument={menuPortalDocument}
                        noMarginReadOnly={noMarginReadOnly}
                        data={formData}
                        onChange={onChange}
                        compact={compact}
                        formFieldBasis={formFieldBasis}
                    />
                </div>
            );
        }
    }
);

Form.displayName = 'Form';
Form.defaultProps = {
    errors: {},
    hookFxns: {},
    headless: false,
    compact: false,
    menuPortalDocument: false,
    noMarginReadOnly: false,
    initDataIfNew: true
};

export default Form;
